import React, { useEffect, useState } from 'react'
import {
  Row, Col, Modal, ModalBody, ModalHeader, ModalFooter,
  Form, FormGroup, Input, FormFeedback, Label,
  Spinner,
} from "reactstrap";
import CustomSelect from '../../../../Components/Common/CustomSelect';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik, validateYupSchema } from 'formik';
import * as Yup from "yup";
import classnames from "classnames";
import { createUser, updateOtherUserPersonalInfo, updateOwnPersonalInfo } from '../../../../slices/auth/settings/thunk';
import { createSelector } from 'reselect';
import { setUserModalData, toggleUserModal } from '../../../../slices/modals/reducer';
import Permission from './Permission';
import country from '../../../../common/country';
import { inviteUserError, resetErrors, setUserPermission } from '../../../../slices/auth/settings/reducer';
import ErrorAlert from '../../../../Components/Common/ErrorAlert';
import Preloader from '../../../../Components/Common/Preloader';
import UploadLogo from '../../../../Components/Common/UploadLogo';
import CustomSelectWithSearch from '../../../../Components/Common/CustomSelectWithSearch';

/**
 * React component for managing user information in a modal.
 * @component
 */
function UserListModal() {
  const dispatch = useDispatch();
  // Select User Modal State
  const selectUserModal = createSelector((state) => state, (state) => ({
    userModal: state.Modal.userModal,
    userModalData: state.Modal.userModalData,
    userModalType: state.Modal.userModalType,
    inviteUserError: state.Settings.inviteUserError,
    inviteUserErrorMsg: state.Settings.inviteUserErrorMsg,
    userPermission: state.Settings.userPermission,
    user: state.Login.user,
    personalInfo: state.Settings.personalInfo
  }));
  const { userModal, userModalData, userModalType, inviteUserError, inviteUserErrorMsg, userPermission, user, personalInfo } = useSelector(selectUserModal);

  const [userPermissionState, setUserPermissionState] = useState([]);

  useEffect(() => {
    if (userModalData) {
      validation.setValues(userModalData);
    }
  }, [userModalData]);

  useEffect(() => {
    setUserPermissionState(userPermission);
  }, [userPermission]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      picture: null,
      firstName: "",
      lastName: "",
      email: "",
      role: "",
      country: "",
    },
    validationSchema: Yup.object({
      firstName: Yup.string().required("Please enter a user first name"),
      lastName: Yup.string().required("Please enter a user last name"),
      email: Yup.string().email().required("Please enter a email"),
      role: Yup.string().required("Please select role"),
      country: Yup.string().required("Please select country"),
    }),
    onSubmit: async (values) => {      
      if (userModalType === "add") {
        const isCreated = await dispatch(createUser(values));
        if (isCreated) {
          closeModal();
        }
      } else if (userModalType === "edit") {
        let isUpdated;
        if (values._id === user.data.userId) {
          isUpdated = await dispatch(updateOwnPersonalInfo({...personalInfo, ...values}));
        } else {
          isUpdated = await dispatch(updateOtherUserPersonalInfo(values));
        }
        if (isUpdated) {
          closeModal();
        }
      }
    }
  });

  /**
  * Handles the change of a selected value in a form input.
  * @param {string} key - The key or name of the form field.
  * @param {any} value - The new selected value for the form field.
  */
  function handleChangeSelect(key, value) {
    validation.setFieldTouched(key, true);
    validation.setFieldValue(key, value);
  }

  function closeModal() {
    dispatch(toggleUserModal())
    setTimeout(() => {
      validation.resetForm();
      dispatch(setUserModalData(null));
      dispatch(setUserPermission([]));
    }, 300);
  }

  return (
    <Modal isOpen={userModal} toggle={closeModal} centered size="lg">
      <ModalHeader className="p-3" toggle={closeModal}>
        {userModalType === "add" ? "Invite" : "Edit"} User
      </ModalHeader>
      <Form className="tablelist-form"
        onSubmit={(e) => {
          e.preventDefault();
          validation.handleSubmit();
          return false;
        }}
        action='#'
      >
        <ModalBody>
          {Array.isArray(userPermissionState) ? 

          <React.Fragment>
            <Row>
              <Col>
                <div className="text-center pb-4">
                  <UploadLogo size='lg'
                    src={validation.values?.picture}
                    onChange={(logo) => { validation.setValues({ ...validation.values, picture: logo }) }} />
                  <p className="mb-0 fw-normal">User Picture</p>
                </div>
              </Col>
            </Row>

            <Row>
              <Col>
                <ErrorAlert errorState={inviteUserError} errorMessage={inviteUserErrorMsg} resetError={resetErrors} />
              </Col>
            </Row>

            <Row>
              <Col>
                <div className="mb-3" id="modal-id">
                  <div className='w-100 d-flex justify-content-between'>
                    <Label htmlFor="firstName" className="form-label">First Name</Label>
                    {validation.touched.firstName && !validation.errors.firstName ? (
                      <FormFeedback valid>Looks good</FormFeedback>
                    ) : null}
                    {validation.touched.firstName && validation.errors.firstName ? (
                      <FormFeedback type="invalid">{validation.errors.firstName}</FormFeedback>
                    ) : null}
                  </div>
                  <Input type="text" id="firstName" name="firstName"
                    placeholder="John"
                    className={"form-control " + classnames({ "is-valid": validation.touched.firstName && !validation.errors.firstName })}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    invalid={validation.touched.firstName && validation.errors.firstName ? true : false}
                    value={validation.values.firstName || ""}
                  />
                </div>
              </Col>
              <Col>
                <div className="mb-3">
                  <div className='w-100 d-flex justify-content-between'>
                    <Label htmlFor="lastName" className="form-label">Last Name</Label>
                    {validation.touched.lastName && !validation.errors.lastName ? (
                      <FormFeedback valid>Looks good</FormFeedback>
                    ) : null}
                    {validation.touched.lastName && validation.errors.lastName ? (
                      <FormFeedback type="invalid">{validation.errors.lastName}</FormFeedback>
                    ) : null}
                  </div>
                  <Input type="text" id="lastName" name="lastName"
                    placeholder="Doe"
                    className={"form-control " + classnames({ "is-valid": validation.touched.lastName && !validation.errors.lastName })}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    invalid={validation.touched.lastName && validation.errors.lastName ? true : false}
                    value={validation.values.lastName || ""}
                  />
                </div>
              </Col>
            </Row>

            <Row>
              <Col>
                <div className="mb-3">
                  <div className='w-100 d-flex justify-content-between'>
                    <Label htmlFor="email" className="form-label">Contact Email</Label>
                    {validation.touched.email && !validation.errors.email ? (
                      <FormFeedback valid>Looks good</FormFeedback>
                    ) : null}
                    {validation.touched.email && validation.errors.email ? (
                      <FormFeedback type="invalid">{validation.errors.email}</FormFeedback>
                    ) : null}
                  </div>
                  <Input type="email" id="email" name="email"
                    placeholder="example@gmail.com"
                    className={"form-control " + classnames({ "is-valid": validation.touched.email && !validation.errors.email })}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    invalid={validation.touched.email && validation.errors.email ? true : false}
                    value={validation.values.email || ""}
                  />
                </div>
              </Col>
              <Col>
                <div className="mb-3">
                  <div className='w-100 d-flex justify-content-between'>
                    <Label htmlFor="role" className="form-label">User Role</Label>
                    {validation.touched.role && !validation.errors.role ? (
                      <FormFeedback valid>Looks good</FormFeedback>
                    ) : null}
                    {validation.touched.role && validation.errors.role ? (
                      <FormFeedback type="invalid">{validation.errors.role}</FormFeedback>
                    ) : null}
                  </div>
                  <FormGroup>
                    <CustomSelect
                      placeholder='Select Role'
                      validation={classnames({ "is-valid": validation.touched.role && !validation.errors.role, "is-invalid": validation.touched.role && validation.errors.role })}
                      options={["Admin", "Designer", "Developer", "Adops"].map(item => { return { name: item, value: item } })}
                      onChange={(item) => handleChangeSelect("role", item.name)}
                      value={(validation.values.role || '')}
                    />
                  </FormGroup>
                </div>
              </Col>
            </Row>

            <Row>
              <Col>
                <div className="mb-3">
                  <div className='w-100 d-flex justify-content-between'>
                    <Label htmlFor="country" className="form-label">Country</Label>
                    {validation.touched.country && !validation.errors.country ? (
                      <FormFeedback valid>Looks good</FormFeedback>
                    ) : null}
                    {validation.touched.country && validation.errors.country ? (
                      <FormFeedback type="invalid">{validation.errors.country}</FormFeedback>
                    ) : null}
                  </div>
                  <FormGroup>
                    <CustomSelectWithSearch
                      placeholder='Select Country'
                      validation={classnames({ "is-valid": validation.touched.country && !validation.errors.country, "is-invalid": validation.touched.country && validation.errors.country })}
                      options={country}
                      onChange={(item) => handleChangeSelect("country", item.name)}
                      value={(validation.values.country || '')}
                    />
                  </FormGroup>
                </div>
              </Col>
            </Row>
            <Row>
              <Col lg="12" md="12" sm="12">
                <h5 className='my-3'>User Permissions</h5>
              </Col>
            </Row>
            <Row>
              <Col>
                <Permission />
              </Col>
            </Row>
          </React.Fragment>
           : <Preloader isComponentLoader={true} />}
        </ModalBody>
        <ModalFooter>
          <div className="hstack gap-2 justify-content-end">
            <button type="button" className="btn btn-light" onClick={closeModal}>Close</button>
            <button type="submit" className="btn btn-success" id="add-btn" disabled={inviteUserError ? true : false}>
              {userModalType === "add" ? "Invite" : "Edit"} User
              {inviteUserError ? <Spinner size='sm' className='ms-2'>Loading...</Spinner> : null}
            </button>
          </div>
        </ModalFooter>
      </Form>
    </Modal>
  )
}

export default UserListModal
